import { GET_ALL_MENU, GET_SALES_DATA, LOAD_MORE_SALES_DATA } from "../types";
import axios from "axios";
import { firebase } from "../../../firebase/initFirebase";
import moment from "moment";
import { store } from "../../store";
import { message } from "antd";
import S3 from "react-aws-s3";

const buildUsedIngredientsFromProductsSold = async (
  Firestore,
  products_sold = [],
) => {
  var ingredientTotals = {};
  var addInventoryItem = (productId, productName, quantity) => {
    var parsedQuantity = parseFloat(quantity ?? 0);

    if (!productId || !(parsedQuantity > 0)) {
      return;
    }

    if (!ingredientTotals[productId]) {
      ingredientTotals[productId] = {
        product: productId,
        product_name: productName || "",
        quantity: 0,
      };
    }

    ingredientTotals[productId].quantity =
      parseFloat(ingredientTotals[productId].quantity) + parsedQuantity;
  };

  await Promise.all(
    (products_sold || []).map(async (item) => {
      var productId = item?.id || item?.product;
      var quantitySold = parseFloat(item?.quantity_sold ?? item?.quantity ?? 0);
      var productName = item?.name || item?.product_name || "";

      if (!productId || !(quantitySold > 0)) {
        return;
      }

      var ingredientsSnapshot = await Firestore.collection("products")
        .doc(productId)
        .collection("ingredients")
        .get();

      if (!ingredientsSnapshot.docs.length) {
        addInventoryItem(productId, productName, quantitySold);
        return;
      }

      ingredientsSnapshot.docs.forEach((ingredientDoc) => {
        var ingredientData = ingredientDoc.data();
        var ingredientProductId = ingredientData?.product;
        var ingredientQuantity = parseFloat(ingredientData?.quantity ?? 0);

        if (!ingredientProductId || !(ingredientQuantity > 0)) {
          return;
        }

        addInventoryItem(
          ingredientProductId,
          ingredientData?.product_name || "",
          ingredientQuantity * quantitySold,
        );
      });
    }),
  );

  return Object.values(ingredientTotals);
};

const getSaleIngredientsForVoid = async (Firestore, record) => {
  var currentUsedIngredients = Array.isArray(record?.used_ingredients)
    ? record.used_ingredients.filter(
        (item) => item?.product && parseFloat(item?.quantity ?? 0) > 0,
      )
    : [];

  if (currentUsedIngredients.length > 0) {
    return currentUsedIngredients;
  }

  return buildUsedIngredientsFromProductsSold(
    Firestore,
    Array.isArray(record?.products_sold) ? record.products_sold : [],
  );
};

const buildVoidInventoryRestoreFields = (
  userID,
  userName,
  source,
  usedIngredients,
) => {
  return {
    void_inventory_restored: true,
    void_inventory_restored_at: moment().format("YYYY-MM-DD HH:mm:ss"),
    void_inventory_restored_by: userID,
    void_inventory_restored_name: userName,
    void_inventory_restore_source: source,
    void_inventory_restore_items_count: Array.isArray(usedIngredients)
      ? usedIngredients.length
      : 0,
  };
};

const combineRestoreIngredients = (usedIngredients = []) => {
  var ingredientTotals = {};

  (usedIngredients || []).forEach((item) => {
    var productId = item?.product;
    var quantity = parseFloat(item?.quantity ?? 0);

    if (!productId || !(quantity > 0)) {
      return;
    }

    if (!ingredientTotals[productId]) {
      ingredientTotals[productId] = {
        product: productId,
        product_name: item?.product_name || "",
        quantity: 0,
      };
    }

    ingredientTotals[productId].quantity =
      parseFloat(ingredientTotals[productId].quantity) + quantity;
  });

  return Object.values(ingredientTotals);
};

const getSaleRestoreEffectiveDate = (record) => {
  return (
    record?.voided_at ||
    record?.sale_date ||
    record?.created_at ||
    moment().format("YYYY-MM-DD HH:mm:ss")
  );
};

const getLastAuditedAtForLocation = async (Firestore, locationId) => {
  var latestAuditAt = null;
  var source = null;

  var monthlyAuditSnapshot = await Firestore.collection("monthlyaudit")
    .where("status", "==", "completed")
    .where("branch", "==", locationId)
    .orderBy("created_at", "desc")
    .limit(1)
    .get();

  if (!monthlyAuditSnapshot.empty) {
    var monthlyAuditData = monthlyAuditSnapshot.docs[0].data();
    latestAuditAt = monthlyAuditData?.created_at || null;
    source = "monthlyaudit";
  }

  try {
    var inventoryAuditSnapshot = await Firestore.collectionGroup(
      "inventory_log",
    )
      .where("reason", "==", "audited")
      .orderBy("created_at", "desc")
      .limit(500)
      .get();

    inventoryAuditSnapshot.docs.forEach((docSnapshot) => {
      var inventoryDocId = docSnapshot.ref.parent?.parent?.id || "";

      if (!inventoryDocId.startsWith(`${locationId}_`)) {
        return;
      }

      var createdAt = docSnapshot.data()?.created_at;

      if (!createdAt) {
        return;
      }

      if (!latestAuditAt || createdAt > latestAuditAt) {
        latestAuditAt = createdAt;
        source = "inventory_log";
      }
    });
  } catch (error) {
    console.log("getLastAuditedAtForLocation inventory log lookup failed", {
      locationId,
      error,
    });
  }

  return {
    lastAuditedAt: latestAuditAt,
    source: source,
  };
};

const sleep = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const isTransientFirestoreThrottleError = (error) => {
  var code = `${error?.code || ""}`.toLowerCase();
  var messageText = `${error?.message || error || ""}`.toLowerCase();

  return (
    code.includes("resource-exhausted") ||
    code.includes("unavailable") ||
    code.includes("deadline-exceeded") ||
    messageText.includes("429") ||
    messageText.includes("too many requests") ||
    messageText.includes("resource exhausted") ||
    messageText.includes("quota")
  );
};

const retryTransientFirestoreOperation = async (
  operation,
  { label, maxAttempts = 4 } = {},
) => {
  var lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt >= maxAttempts || !isTransientFirestoreThrottleError(error)) {
        throw error;
      }

      var delay = 500 * Math.pow(2, attempt - 1);
      console.log("retrying transient firestore operation", {
        label,
        attempt,
        nextAttempt: attempt + 1,
        delay,
        code: error?.code || null,
        message: error?.message || `${error}`,
      });
      await sleep(delay);
    }
  }

  throw lastError;
};

const restoreVoidedSaleInventoryTransaction = async ({
  Firestore,
  record,
  usedIngredients,
  userID,
  userName,
  source,
  extraSaleFields = {},
}) => {
  var saleRef = Firestore.collection("sales").doc(record.id);
  var restoreItems = combineRestoreIngredients(usedIngredients);
  var restoredAt = moment().format("YYYY-MM-DD HH:mm:ss");
  var skippedResult = null;

  var restoreFields = buildVoidInventoryRestoreFields(
    userID,
    userName,
    source,
    restoreItems,
  );
  restoreFields.void_inventory_restored_at = restoredAt;

  if (
    (!Array.isArray(record?.used_ingredients) ||
      record.used_ingredients.length === 0) &&
    restoreItems.length > 0
  ) {
    restoreFields.used_ingredients = restoreItems;
  }

  var result = await Firestore.runTransaction(async (transaction) => {
    var saleSnap = await transaction.get(saleRef);

    if (!saleSnap.exists) {
      throw new Error("Sale no longer exists.");
    }

    var currentSale = saleSnap.data();

    if (currentSale?.void_inventory_restored === true) {
      skippedResult = {
        skipped: true,
        alreadyRestored: true,
        restoreItems: Array.isArray(currentSale?.used_ingredients)
          ? currentSale.used_ingredients
          : restoreItems,
      };
      return skippedResult;
    }

    if (source === "void-sale" && currentSale?.status === "voided") {
      skippedResult = {
        skipped: true,
        alreadyVoided: true,
        alreadyRestored: false,
        restoreItems: Array.isArray(currentSale?.used_ingredients)
          ? currentSale.used_ingredients
          : restoreItems,
      };
      return skippedResult;
    }

    if (source !== "void-sale" && currentSale?.status !== "voided") {
      throw new Error("Only voided sales can be restored.");
    }

    var inventoryUpdates = [];

    for (let index = 0; index < restoreItems.length; index++) {
      var ingredient = restoreItems[index];
      var inventoryID = `${record.branch}_${ingredient.product}`;
      var productRef = Firestore.collection("products").doc(ingredient.product);
      var inventoryRef = Firestore.collection("inventory").doc(inventoryID);
      var inventorySnap = await transaction.get(inventoryRef);
      var currentInventory = inventorySnap.exists ? inventorySnap.data() : null;
      var currentQuantity = parseFloat(currentInventory?.quantity ?? 0);
      var restock = parseFloat(currentInventory?.restock ?? 0);
      var newQuantity = currentQuantity + parseFloat(ingredient.quantity);
      var updateForm = {
        updated_at: restoredAt,
        updated_by: userID,
        updated_name: userName,
      };
      var inventoryForm = {
        ...updateForm,
        reason: "voided sale",
        restock: restock,
        quantity: newQuantity,
      };

      if (!inventorySnap.exists) {
        inventoryForm = {
          ...inventoryForm,
          created_at: restoredAt,
          location: record.branch,
          location_name: record.branch_name,
          product: ingredient.product,
          product_name: ingredient.product_name,
        };
      }

      var inventoryLogRef = inventoryRef.collection("inventory_log").doc();
      var inventoryLogForm = {
        created_at: restoredAt,
        reason: "voided sale",
        created_by: userID,
        created_name: userName,
        quantity: newQuantity,
        restock: restock,
      };
      var prodForm = {
        ...updateForm,
      };
      prodForm[`${record.branch}_count`] = newQuantity;
      prodForm[`${record.branch}_restock`] = restock;

      inventoryUpdates.push({
        productRef,
        inventoryRef,
        inventoryLogRef,
        prodForm,
        inventoryForm,
        inventoryLogForm,
      });
    }

    inventoryUpdates.forEach((item) => {
      transaction.set(item.productRef, item.prodForm, { merge: true });
      transaction.set(item.inventoryRef, item.inventoryForm, { merge: true });
      transaction.set(item.inventoryLogRef, item.inventoryLogForm);
    });

    transaction.set(
      saleRef,
      {
        ...restoreFields,
        ...extraSaleFields,
      },
      { merge: true },
    );

    return {
      skipped: false,
      alreadyRestored: false,
      restoreItems: restoreItems,
      restoreFields: {
        ...restoreFields,
        ...extraSaleFields,
      },
    };
  });

  return skippedResult || result;
};

const voidSaleInventoryBatchNoRead = async ({
  Firestore,
  record,
  usedIngredients,
  userID,
  userName,
  extraSaleFields = {},
}) => {
  var saleRef = Firestore.collection("sales").doc(record.id);
  var restoreItems = combineRestoreIngredients(usedIngredients);
  var restoredAt = moment().format("YYYY-MM-DD HH:mm:ss");
  var fieldValue = firebase.firestore.FieldValue;

  var restoreFields = buildVoidInventoryRestoreFields(
    userID,
    userName,
    "void-sale",
    restoreItems,
  );
  restoreFields.void_inventory_restored_at = restoredAt;

  if (
    (!Array.isArray(record?.used_ingredients) ||
      record.used_ingredients.length === 0) &&
    restoreItems.length > 0
  ) {
    restoreFields.used_ingredients = restoreItems;
  }

  await saleRef.set(extraSaleFields, { merge: true });
  console.log("voidSale sale status committed", {
    saleId: record.id,
    status: extraSaleFields?.status || null,
  });

  var batch = Firestore.batch();

  restoreItems.forEach((ingredient) => {
    var inventoryID = `${record.branch}_${ingredient.product}`;
    var productRef = Firestore.collection("products").doc(ingredient.product);
    var inventoryRef = Firestore.collection("inventory").doc(inventoryID);
    var inventoryLogRef = inventoryRef
      .collection("inventory_log")
      .doc(`void-sale-${record.id}-${ingredient.product}`);
    var quantity = parseFloat(ingredient.quantity);
    var updateForm = {
      updated_at: restoredAt,
      updated_by: userID,
      updated_name: userName,
    };

    batch.set(
      productRef,
      {
        ...updateForm,
        [`${record.branch}_count`]: fieldValue.increment(quantity),
      },
      { merge: true },
    );

    batch.set(
      inventoryRef,
      {
        ...updateForm,
        created_at: restoredAt,
        location: record.branch,
        location_name: record.branch_name,
        product: ingredient.product,
        product_name: ingredient.product_name,
        reason: "voided sale",
        quantity: fieldValue.increment(quantity),
      },
      { merge: true },
    );

    batch.set(
      inventoryLogRef,
      {
        created_at: restoredAt,
        reason: "voided sale",
        created_by: userID,
        created_name: userName,
        product: ingredient.product,
        product_name: ingredient.product_name,
        quantity_change: quantity,
        sale: record.id,
      },
      { merge: true },
    );
  });

  batch.set(saleRef, restoreFields, { merge: true });

  try {
    await batch.commit();
  } catch (error) {
    console.error("voidSale inventory restore failed after sale voided", {
      saleId: record.id,
      error,
    });
    await saleRef.set(
      {
        void_inventory_restored: false,
        void_inventory_restore_error:
          error?.message || "Inventory restore failed after voiding sale.",
        void_inventory_restore_error_at: moment().format("YYYY-MM-DD HH:mm:ss"),
      },
      { merge: true },
    );

    return {
      skipped: false,
      alreadyRestored: false,
      inventoryRestoreFailed: true,
      restoreItems: restoreItems,
      restoreFields: {
        ...restoreFields,
        ...extraSaleFields,
      },
    };
  }

  return {
    skipped: false,
    alreadyRestored: false,
    restoreItems: restoreItems,
    restoreFields: {
      ...restoreFields,
      ...extraSaleFields,
    },
  };
};

export const restoreVoidedSaleInventory = (record, options = {}) => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var Firestore = firebase.firestore();
    var userID = reduxState.auth.user.id;
    var userName = reduxState.auth.user.name;
    var source = options?.source || "manual-void-restore";
    var skipAlreadyRestoredCheck = options?.skipAlreadyRestoredCheck === true;
    var ignoreAuditBoundary = options?.ignoreAuditBoundary === true;
    var restoreEffectiveDate = getSaleRestoreEffectiveDate(record);
    var shouldCheckAuditBoundary = source !== "void-sale";
    var auditBoundary = shouldCheckAuditBoundary
      ? await getLastAuditedAtForLocation(Firestore, record.branch)
      : {
          lastAuditedAt: null,
          source: null,
        };

    if (
      record?.status !== "voided" &&
      source !== "void-sale" &&
      !skipAlreadyRestoredCheck
    ) {
      throw new Error("Only voided sales can be restored.");
    }

    if (record?.void_inventory_restored === true && !skipAlreadyRestoredCheck) {
      return {
        skipped: true,
        alreadyRestored: true,
        restoreItems: Array.isArray(record?.used_ingredients)
          ? record.used_ingredients
          : [],
      };
    }

    if (
      !ignoreAuditBoundary &&
      source !== "void-sale" &&
      auditBoundary?.lastAuditedAt &&
      restoreEffectiveDate < auditBoundary.lastAuditedAt
    ) {
      console.log("restoreVoidedSaleInventory skipped by audit boundary", {
        saleId: record.id,
        branch: record.branch,
        restoreEffectiveDate,
        lastAuditedAt: auditBoundary.lastAuditedAt,
      });
      return {
        skipped: true,
        blockedByAudit: true,
        alreadyRestored: false,
        restoreItems: [],
        lastAuditedAt: auditBoundary.lastAuditedAt,
      };
    }

    var usedIngredients = await getSaleIngredientsForVoid(Firestore, record);
    console.log(
      source === "void-sale"
        ? "voidSale inventory restore start"
        : "restoreVoidedSaleInventory start",
      {
        saleId: record.id,
        branch: record.branch,
        restoreEffectiveDate,
        restoreItemCount: usedIngredients.length,
        lastAuditedAt: auditBoundary?.lastAuditedAt || null,
      },
    );

    if (source === "void-sale") {
      return retryTransientFirestoreOperation(
        () =>
          voidSaleInventoryBatchNoRead({
            Firestore,
            record,
            usedIngredients,
            userID,
            userName,
            extraSaleFields: options?.extraSaleFields || {},
          }),
        {
          label: `voidSaleInventoryBatch:${record.id || ""}`,
        },
      );
    }

    return retryTransientFirestoreOperation(
      () =>
        restoreVoidedSaleInventoryTransaction({
          Firestore,
          record,
          usedIngredients,
          userID,
          userName,
          source,
          extraSaleFields: options?.extraSaleFields || {},
        }),
      {
        label: `restoreVoidedSaleInventory:${record.id || ""}`,
      },
    );
  };
};

export const getVoidedSalesInventoryRestoreCandidates = (filters = {}) => {
  return async (dispatch) => {
    var Firestore = firebase.firestore();
    var limit = filters?.limit || 500;

    var dataRef = Firestore.collection("sales")
      .where("status", "==", "voided")
      .orderBy("voided_at", "desc");

    dataRef = dataRef.limit(limit);

    var documentSnapshots = await dataRef.get();
    var auditBoundaryCache = {};
    var sales = await Promise.all(
      documentSnapshots.docs.map(async (salesItem) => {
        var salesData = salesItem.data();
        var branchId = salesData?.branch;
        var restoreEffectiveDate = getSaleRestoreEffectiveDate(salesData);
        if (!auditBoundaryCache[branchId]) {
          auditBoundaryCache[branchId] = await getLastAuditedAtForLocation(
            Firestore,
            branchId,
          );
        }
        var auditBoundary = auditBoundaryCache[branchId];
        var restoreItems = await getSaleIngredientsForVoid(Firestore, {
          id: salesItem.id,
          ...salesData,
        });
        var blockedByAudit =
          auditBoundary?.lastAuditedAt &&
          restoreEffectiveDate < auditBoundary.lastAuditedAt;

        return {
          key: salesItem.id,
          id: salesItem.id,
          ...salesData,
          restore_effective_date: restoreEffectiveDate,
          location_last_audited_at: auditBoundary?.lastAuditedAt || null,
          location_last_audit_source: auditBoundary?.source || null,
          restore_blocked_by_audit: blockedByAudit,
          void_inventory_restore_items: restoreItems,
          void_inventory_restore_items_count: restoreItems.length,
          void_inventory_restored: salesData?.void_inventory_restored === true,
        };
      }),
    );

    var pendingSales = sales.filter(
      (item) => item.void_inventory_restored !== true,
    );
    var eligibleSales = pendingSales.filter(
      (item) =>
        item.restore_blocked_by_audit !== true &&
        item.void_inventory_restore_items_count > 0,
    );

    return {
      sales: pendingSales,
      eligibleSales: eligibleSales,
      allSales: sales,
      summary: {
        totalMatched: sales.length,
        pendingCount: pendingSales.length,
        eligibleCount: eligibleSales.length,
        blockedByAuditCount: pendingSales.filter(
          (item) => item.restore_blocked_by_audit === true,
        ).length,
        restoredCount: sales.filter(
          (item) => item.void_inventory_restored === true,
        ).length,
      },
    };
  };
};

export const getSalesData = (props) => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var userRole = reduxState.auth.user.role;
    var useremail = reduxState.auth.user.email;
    var userLocation = reduxState.auth.user.branch;
    var userLocationName = reduxState.auth.user.branch_name;

    var Firestore = firebase.firestore();
    var dataRef = Firestore.collection("sales");
    console.log(props);
    var salesStatus = props?.salesStatus;

    dataRef = dataRef.orderBy("sale_date", "desc");
    if (salesStatus) {
      dataRef = dataRef.where("status", "==", salesStatus);
    } else if (!props.showVoided) {
      dataRef = dataRef.where("status", "==", "completed");
    }

    if (
      userRole !== "admin" &&
      userRole !== "manager" &&
      userRole !== "warehouse"
    ) {
      dataRef = dataRef.where("branch", "==", userLocation);
    }

    console.log(props);
    // if(props.searchText){
    //   dataRef = dataRef.orderBy('name').startAt(props.searchText).endAt(props.searchText + '\uf8ff')
    // }
    // if(props.filters){
    //   if(props.filters.length > 0){
    //     props.filters.map(item=>{
    //       console.log(item)
    //       dataRef = dataRef.orderBy(item.dataIndex).startAt(item.value).endAt(item.value + '\uf8ff')
    //     })
    //   }
    // }

    dataRef = dataRef.limit(40);

    var documentSnapshots = await dataRef.get();

    var docs = documentSnapshots.docs;
    var lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
    var dataArray = [];

    await Promise.all(
      docs.map(async (salesItem) => {
        var newOrder = {
          key: salesItem.id,
          id: salesItem.id,
          ...salesItem.data(),
        };

        dataArray.push(newOrder);
      }),
    );
    dispatch({
      type: GET_SALES_DATA,
      payload: {
        dataArray: dataArray,
        lastVisible: lastVisible,
        currentProps: props,
      },
    });
  };
};

export const loadMoreSalesData = () => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var userRole = reduxState.auth.user.role;
    var useremail = reduxState.auth.user.email;
    var currentLastItem = reduxState.dailysales.lastVisible;
    var userLocation = reduxState.auth.user.branch;
    var userLocationName = reduxState.auth.user.branch_name;

    var currentProps = reduxState.dailysales.currentProps;
    var Firestore = firebase.firestore();
    var salesStatus = currentProps?.salesStatus;

    var dataRef = Firestore.collection(currentProps.apiurl);

    dataRef = dataRef.orderBy("sale_date", "desc");

    if (salesStatus) {
      dataRef = dataRef.where("status", "==", salesStatus);
    } else if (!currentProps?.showVoided) {
      dataRef = dataRef.where("status", "==", "completed");
    }
    if (
      userRole !== "admin" &&
      userRole !== "manager" &&
      userRole !== "warehouse"
    ) {
      dataRef = dataRef.where("branch", "==", userLocation);
    }

    if (currentLastItem) {
      dataRef = dataRef.startAfter(currentLastItem);

      // if(currentProps.searchText){
      //   dataRef = dataRef.where('name', '>=', currentProps.searchText).where('name', '<=', currentProps.searchText+ '\uf8ff')
      // }

      dataRef = dataRef.limit(50);

      var documentSnapshots = await dataRef.get();
      var docs = documentSnapshots.docs;
      if (docs) {
        var lastVisible =
          documentSnapshots.docs[documentSnapshots.docs.length - 1];
        var dataArray = [];
        await Promise.all(
          docs.map(async (salesItem) => {
            var newOrder = {
              key: salesItem.id,
              id: salesItem.id,
              ...salesItem.data(),
            };

            dataArray.push(newOrder);
          }),
        );
        if (lastVisible && currentLastItem !== lastVisible) {
          dispatch({
            type: LOAD_MORE_SALES_DATA,
            payload: { dataArray: dataArray, lastVisible: lastVisible },
          });
        } else {
          message.error("No More Items To Load");
        }
      } else {
        message.error("No More Items To Load");
      }
    }
  };
};

export const getMenuItems = (props) => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var userRole = reduxState.auth.user.role;
    var useremail = reduxState.auth.user.email;
    var userLocation = reduxState.auth.user.branch;
    var Firestore = firebase.firestore();

    var userLocationName = reduxState.auth.user.branch_name;

    var applyBrandFilter = false;
    if (userRole == "oic") {
      applyBrandFilter = true;
    }
    var productdataRef = Firestore.collection("products");
    // console.log(props)
    productdataRef = productdataRef.orderBy("category_name", "desc");

    var productdataRef1 = productdataRef.where("menu_item", "==", true);

    var documentSnapshots1 = await productdataRef1.get();

    var proddocs1 = documentSnapshots1.docs;

    var productdataRef2 = productdataRef.where("is_resell", "==", true);

    var documentSnapshots2 = await productdataRef2.get();

    var proddocs2 = documentSnapshots2.docs;

    var proddocs = [...proddocs1, ...proddocs2];

    console.log(proddocs);

    var newProds = [];
    await Promise.all(
      proddocs.map(async (item) => {
        var prodingredientsRef = Firestore.collection("products")
          .doc(item.id)
          .collection("ingredients");
        var ingredientsdocs = await prodingredientsRef.get();
        var newinds = [];
        if (ingredientsdocs.docs) {
          ingredientsdocs.docs.map((ind) => {
            if (ind.data()?.product) {
              newinds.push(ind.data());
            }
          });
        }
        var newProd = {
          key: item.id,
          id: item.id,
          ingredients: newinds,
          ...item.data(),
        };

        // if (
        //   applyBrandFilter &&
        //   newProd?.brand_name &&
        //   newProd.brand_name.length > 0
        // ) {
        //   if (
        //     userLocationName == "Pause" &&
        //     newProd.brand_name.includes("Pause")
        //   ) {
        //     newProds.push(newProd);
        //   } else if (
        //     userLocationName !== "Pause" &&
        //     newProd.brand_name.includes("Lala and Co")
        //   ) {
        //     newProds.push(newProd);
        //   }
        // } else {

        // }
        newProds.push(newProd);
      }),
    );
    dispatch({ type: GET_ALL_MENU, payload: newProds });
  };
};

export const setSale = (
  products_sold,
  save_ingredients,
  createDate,
  sell_total,
  extradata,
) => {
  return async (dispatch) => {
    // 1) Normalize at log payload (optional)
    // const payload = {
    //   products_sold,
    //   save_ingredients,
    //   createDate,
    //   sell_total,
    //   extradata,
    // };
    // console.log(payload);

    // 2) Safely parse/format date kahit string/Date/Moment
    //    (gamitin dayjs or moment; below assumes moment is available)
    const saleDate = moment(createDate).isValid()
      ? moment(createDate).format("YYYY-MM-DD HH:mm:ss")
      : moment().format("YYYY-MM-DD HH:mm:ss");

    // 3) Strip undefined fields para hindi magalit si Firestore
    const omitUndefined = (obj) =>
      Object.fromEntries(
        Object.entries(obj || {}).filter(([_, v]) => v !== undefined),
      );

    const cleanExtra = omitUndefined(extradata);
    // Optional: ensure nullable keys are null rather than undefined
    if (cleanExtra.seller === undefined) cleanExtra.seller = null;

    // 4) Firestore + user context
    const reduxState = store.getState();
    const Firestore = firebase.firestore();
    const userID = reduxState.auth.user.id;
    const userName = reduxState.auth.user.name;
    const userLocation = reduxState.auth.user.branch;
    const userLocationName = reduxState.auth.user.branch_name;

    const salesRef = Firestore.collection("sales").doc();

    // 5) Build sales data (no undefined)
    const salesData = {
      status: "completed",
      sale_date: saleDate, // string na
      created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
      created_by: userID,
      created_name: userName,
      branch: userLocation,
      branch_name: userLocationName,
      products_sold,
      used_ingredients: save_ingredients,
      total_sell_price: sell_total,
      ...cleanExtra, // walang undefined
    };

    console.log(salesData);

    await salesRef.set(salesData, { merge: true });
    return "oks";
  };
};

export const voidSale = (record) => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var userID = reduxState.auth.user.id;
    var userName = reduxState.auth.user.name;
    var restoreResult = await dispatch(
      restoreVoidedSaleInventory(record, {
        source: "void-sale",
        skipAlreadyRestoredCheck: true,
        extraSaleFields: {
          status: "voided",
          voided_at: moment().format("YYYY-MM-DD HH:mm:ss"),
          voided_by: userID,
          voided_name: userName,
        },
      }),
    );

    if (restoreResult?.alreadyVoided || restoreResult?.alreadyRestored) {
      message.info("This sale has already been voided.");
    } else if (restoreResult?.inventoryRestoreFailed) {
      message.warning(
        "Sale was voided, but inventory restore failed. Use the voided sale restore tool after checking permissions/quota.",
      );
    }

    return "oks";
  };
};

export const setSaleDate = (record, newdate) => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var Firestore = firebase.firestore();
    var userID = reduxState.auth.user.id;
    var userName = reduxState.auth.user.name;
    var userLocation = reduxState.auth.user.branch;
    var userLocationName = reduxState.auth.user.branch_name;

    var salesRef = Firestore.collection("sales").doc(record.id);

    var salesData = {
      sale_date: moment(newdate).format("YYYY-MM-DD HH:mm:ss"),
      sale_date_changed: userID,
      sale_date_changed_name: userName,
    };
    console.log(salesData);

    var salesndone = await salesRef.set(salesData, { merge: true });

    return "oks";
  };
};

export const fixSaleNoIngredients = (
  currentSalesOrder,
  products_sold,
  save_ingredients,
) => {
  return async (dispatch) => {
    var reduxState = store.getState();
    var Firestore = firebase.firestore();
    var userID = reduxState.auth.user.id;
    var userName = reduxState.auth.user.name;
    var userLocation = reduxState.auth.user.branch;
    var userLocationName = reduxState.auth.user.branch_name;

    var salesRef = Firestore.collection("sales").doc(currentSalesOrder.id);

    console.log(save_ingredients);
    var salesData = {
      products_sold: products_sold,
      used_ingredients: save_ingredients,
    };
    console.log(salesData);

    var salesndone = await salesRef.set(salesData, { merge: true });

    return "oks";
  };
};

export const getProductSalesData = (props) => {
  return async (dispatch) => {
    try {
      var reduxState = store.getState();
      var userRole = reduxState.auth.user.role;
      var useremail = reduxState.auth.user.email;
      var userLocation = reduxState.auth.user.branch;
      var userLocationName = reduxState.auth.user.branch_name;

      var Firestore = firebase.firestore();
      var dataRef = Firestore.collection("sales");

      console.log("props", props);

      dataRef = dataRef.orderBy("sale_date", "desc");
      dataRef = dataRef.where("status", "==", "completed");

      if (
        userRole !== "admin" &&
        userRole !== "manager" &&
        userRole !== "manager"
      ) {
        dataRef = dataRef.where("branch", "==", userLocation);
      }

      // Filter by branch if provided in props
      if (props.branch) {
        dataRef = dataRef.where("branch", "==", props.branch);
      }

      // Filter by sale_date range if provided in props
      if (props.startDate && props.endDate) {
        dataRef = dataRef.where("sale_date", ">=", props.startDate);
        dataRef = dataRef.where("sale_date", "<=", props.endDate);
      }

      const batchSize = 100; // Adjust the batch size as needed
      const productToSearch = props.product; // Assuming 'props.product' contains the product ID to search for
      let lastDocument = null;
      let dataArray = [];

      while (true) {
        let query = dataRef.limit(batchSize);

        if (lastDocument) {
          query = query.startAfter(lastDocument);
        }

        const querySnapshot = await query.get();
        if (querySnapshot.empty) {
          break;
        }

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log(data);
          if (data.products_sold.some((item) => item.id == productToSearch)) {
            const sale_item = data.products_sold.filter(
              (item) => item.id == productToSearch,
            );

            const prodSaleData = {
              branch: data.branch,
              branch_name: data.branch_name,
              created_at: data.created_at,
              sale_date: data.sale_date,
              ...sale_item[0],
            };
            console.log("prodSaleData", prodSaleData);
            dataArray.push(prodSaleData);
          }
        });

        lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
        const lastDocumentData = lastDocument.data();
        message.loading(
          `Loading Data - last loaded (${lastDocumentData.sale_date})`,
        );
      }

      console.log("Filtered Documents: ", dataArray);

      // Optionally dispatch the data to Redux store if needed
      dispatch({
        type: "SET_PRODUCT_SALES_DATA",
        payload: dataArray,
      });

      return dataArray;
    } catch (error) {
      console.error("Error getting documents: ", error);
      // Handle the error as needed
      throw error; // Propagate the error
    }
  };
};
